import * as React from 'react';
import { MdAttachMoney, MdMedicalServices, MdClose } from 'react-icons/md';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { getProviderIncentiveReport } from '../../Services/PayrollService';
import { loadIncentiveInsurances } from '../../Services/IncentiveRateService';

const CPTS_PER_PAGE = 9;

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const buildOptions = (values = []) => {
	const unique = Array.from(new Set((values || []).filter(Boolean)));
	return unique.map((value) => ({ value, label: value === 'ALL' ? 'All' : value }));
};

const formatDateParam = (d) => {
	if (!d) return '';
	const date = d instanceof Date ? d : new Date(d);
	if (Number.isNaN(date.getTime())) return '';
	const mm = String(date.getMonth() + 1).padStart(2, '0');
	const dd = String(date.getDate()).padStart(2, '0');
	const yyyy = date.getFullYear();
	return `${mm}/${dd}/${yyyy}`;
};

export default function IncentiveReportSection({ token, organizationId, providerName, startDate, endDate, facilities }) {
	const [insuranceOptions, setInsuranceOptions] = React.useState([]);
	const [optionsLoading, setOptionsLoading] = React.useState(false);

	const [insurance, setInsurance] = React.useState('ALL');
	const [facility, setFacility] = React.useState('ALL');
	const [selectedCpt, setSelectedCpt] = React.useState(null);
	const [cptSearch, setCptSearch] = React.useState('');

	const [report, setReport] = React.useState(null);
	const [loading, setLoading] = React.useState(false);
	const [cptPage, setCptPage] = React.useState(1);

	const facilityOptions = React.useMemo(
		() => buildOptions(['ALL', ...(facilities || []).map((f) => f?.facility_name)]),
		[facilities],
	);

	React.useEffect(() => {
		if (facility !== 'ALL' && !facilityOptions.some((option) => option.value === facility)) {
			setFacility('ALL');
		}
	}, [facilityOptions, facility]);

	React.useEffect(() => {
		if (!token || !organizationId) return;
		let cancelled = false;
		(async () => {
			try {
				setOptionsLoading(true);
				const insuranceRes = await loadIncentiveInsurances(token, organizationId);
				if (cancelled) return;
				setInsuranceOptions(buildOptions(insuranceRes?.is_success ? insuranceRes.data : ['ALL']));
			} catch (error) {
				if (!cancelled) setInsuranceOptions([{ value: 'ALL', label: 'All' }]);
			} finally {
				if (!cancelled) setOptionsLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [token, organizationId]);

	React.useEffect(() => {
		if (!token || !organizationId || !providerName || !startDate || !endDate) {
			setReport(null);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				setLoading(true);
				const res = await getProviderIncentiveReport(token, organizationId, {
					providerName,
					startDate: formatDateParam(startDate),
					endDate: formatDateParam(endDate),
					insurance,
					facility,
				});
				if (cancelled) return;
				setReport(res?.is_success ? res.data : null);
				setCptPage(1);
			} catch (error) {
				if (!cancelled) setReport(null);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [token, organizationId, providerName, startDate, endDate, insurance, facility]);

	const cptBreakdown = React.useMemo(() => report?.cpt_breakdown || [], [report]);

	React.useEffect(() => {
		if (selectedCpt && !cptBreakdown.some((row) => row.cpt_code === selectedCpt)) {
			setSelectedCpt(null);
		}
	}, [cptBreakdown, selectedCpt]);

	const matchStatus = report?.provider_match_status;
	const percentageNote =
		matchStatus === 'ambiguous'
			? 'Multiple provider profiles match this name — set incentive percentage manually to resolve.'
			: matchStatus === 'not_found'
				? 'No matching provider profile found for this name.'
				: matchStatus === 'matched_no_percentage'
					? 'Matched a provider profile, but no incentive percentage is set for them yet.'
					: null;

	const searchTerm = cptSearch.trim().toLowerCase();
	const filteredCpts = searchTerm
		? cptBreakdown.filter((row) => row.cpt_code.toLowerCase().includes(searchTerm))
		: cptBreakdown;

	const totalCptPages = Math.max(1, Math.ceil(filteredCpts.length / CPTS_PER_PAGE));
	const safeCptPage = Math.min(cptPage, totalCptPages);
	const cptPageStart = (safeCptPage - 1) * CPTS_PER_PAGE;
	const pagedCpts = filteredCpts.slice(cptPageStart, cptPageStart + CPTS_PER_PAGE);

	const selectedRow = selectedCpt ? cptBreakdown.find((row) => row.cpt_code === selectedCpt) : null;
	const viewUnits = selectedRow ? selectedRow.units : (report?.total_units ?? 0);
	const viewUnmatched = selectedRow ? selectedRow.unmatched_units : (report?.unmatched_rate_units ?? 0);
	const viewIncentive = selectedRow ? selectedRow.incentive_amount : (report?.incentive_amount ?? 0);
	const viewRateTotal = selectedRow ? selectedRow.amount : (report?.total_amount ?? 0);
	const incentivePercentage = report?.incentive_percentage;

	const unmatchedNote =
		viewUnmatched > 0
			? `${viewUnmatched} of ${viewUnits} unit${viewUnits === 1 ? '' : 's'} ${viewUnmatched === 1 ? 'has' : 'have'} no configured rate for its insurance/CPT/facility combination and ${viewUnmatched === 1 ? 'is' : 'are'} excluded from the incentive amount.`
			: null;

	return (
		<div>
			<div className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4">
				<div>
					<h5 className="fw-bold mb-1 d-flex flex-wrap align-items-center gap-2 hc-text-primary">
						<MdAttachMoney size={20} />
						Incentive Report
					</h5>
					<p className="text-secondary mb-0 small">
						Units, standard rate, and incentive payout for this provider across the selected date range.
					</p>
				</div>
			</div>

			<div className="row g-3 mb-4">
				<div className="col-12 col-xl-4">
					<label className="form-label fw-semibold mb-2" htmlFor="incentive-cpt-search">
						Search CPT Code
					</label>
					<div className="position-relative">
						<input
							id="incentive-cpt-search"
							type="text"
							className="form-control"
							style={{ height: 37, paddingRight: 34 }}
							placeholder="Search CPT code"
							value={cptSearch}
							onChange={(event) => {
								setCptSearch(event.target.value);
								setCptPage(1);
							}}
						/>
						{cptSearch ? (
							<button
								type="button"
								className="border-0 bg-transparent p-0 text-secondary d-flex align-items-center position-absolute top-50 translate-middle-y"
								style={{ right: 10 }}
								aria-label="Clear CPT search"
								onClick={() => {
									setCptSearch('');
									setCptPage(1);
								}}
							>
								<MdClose size={16} />
							</button>
						) : null}
					</div>
				</div>
				<div className="col-12 col-md-6 col-xl-4">
					<SearchableDropdown
						label="Insurance"
						placeholder="All insurances"
						options={insuranceOptions}
						value={insurance}
						onChange={(value) => setInsurance(value || 'ALL')}
						disabled={optionsLoading}
						loading={optionsLoading}
						emptyMessage="No insurances found"
					/>
				</div>
				<div className="col-12 col-md-6 col-xl-4">
					<SearchableDropdown
						label="Facility"
						placeholder="All facilities"
						options={facilityOptions}
						value={facility}
						onChange={(value) => setFacility(value || 'ALL')}
						emptyMessage="No facilities found"
					/>
				</div>
			</div>

			{/* Total for the insurance/facility filters (or the selected code), ahead of the per-code breakdown. */}
			<div
				className="service-card px-3 py-3 rounded-3 w-100 d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 hc-text-primary"
				style={{ cursor: 'default' }}
			>
				<div className="min-w-0">
					<div className="fw-bold d-flex align-items-center gap-2 text-break">
						<MdAttachMoney size={18} className="flex-shrink-0" />
						{selectedRow ? `Total Incentive Amount · ${selectedRow.cpt_code}` : 'Total Incentive Amount'}
					</div>
					<div className="small" style={{ opacity: 0.7 }}>
						{loading
							? 'Calculating...'
							: [
								`${viewUnits} ${viewUnits === 1 ? 'unit' : 'units'}`,
								viewUnmatched ? `${viewUnmatched} unpriced` : null,
								incentivePercentage != null
									? `${incentivePercentage}% of ${currencyFormatter.format(viewRateTotal ?? 0)} at standard rates`
									: null,
							].filter(Boolean).join(' · ')}
					</div>
				</div>
				<div className="fw-bold" style={{ fontSize: '1.6rem' }}>
					{loading ? (
						<span className="placeholder-glow"><span className="placeholder rounded" style={{ width: 120 }} /></span>
					) : (
						currencyFormatter.format(viewIncentive ?? 0)
					)}
				</div>
			</div>

			<div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
				<div>
					<h6 className="fw-bold mb-1 d-flex align-items-center gap-2 hc-text-primary">
						<MdMedicalServices size={18} />
						CPT Codes Seen
					</h6>
					<p className="text-secondary mb-0 small">
						Select a code to narrow the total above, or leave unselected for all codes.
					</p>
				</div>
				<div className="d-flex align-items-center gap-2">
					{selectedCpt ? (
						<button
							type="button"
							className="btn btn-sm btn-light border fw-semibold rounded-pill px-3"
							onClick={() => setSelectedCpt(null)}
						>
							Clear
						</button>
					) : null}
					{!loading && cptBreakdown.length ? (
						<span className="badge rounded-pill bg-primary-subtle text-primary fw-semibold px-3 py-2">
							{searchTerm ? `${filteredCpts.length} of ${cptBreakdown.length}` : `${cptBreakdown.length} total`}
						</span>
					) : null}
				</div>
			</div>

			{loading ? (
				<div className="row g-3">
					{Array.from({ length: 3 }).map((_, idx) => (
						<div className="col-12 col-md-6 col-xl-4" key={idx}>
							<div
								className="service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 hc-text-primary placeholder-glow"
								style={{ minHeight: '62px', display: 'flex', alignItems: 'center', cursor: 'default' }}
							>
								<span className="placeholder w-50 rounded" />
							</div>
						</div>
					))}
				</div>
			) : !filteredCpts.length ? (
				<div className="rounded-4 border bg-light-subtle p-4 text-center text-muted">
					<MdMedicalServices size={28} className="mb-2 text-secondary" />
					<div className="small">
						{cptBreakdown.length
							? `No CPT code matches "${cptSearch.trim()}".`
							: 'No CPT codes were billed by this provider for the selected filters and date range.'}
					</div>
				</div>
			) : (
				<>
					<div className="row g-3">
						{pagedCpts.map((row) => {
							const isSelected = row.cpt_code === selectedCpt;
							return (
								<div className="col-12 col-md-6 col-xl-4" key={row.cpt_code}>
									<button
										type="button"
										className={`service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 d-flex align-items-center justify-content-between gap-2${isSelected ? ' active' : ' hc-text-primary'}`}
										style={{ minHeight: '62px' }}
										onClick={() => setSelectedCpt(isSelected ? null : row.cpt_code)}
									>
										<span className="text-truncate min-w-0" title={row.cpt_code}>{row.cpt_code}</span>
										<span className="d-flex flex-column align-items-end flex-shrink-0 lh-sm text-end">
											<span style={{ fontSize: '0.95rem' }}>
												{row.amount ? currencyFormatter.format(row.incentive_amount ?? 0) : '—'}
											</span>
											<span style={{ fontSize: '0.72rem', opacity: 0.7 }}>
												{row.units} {row.units === 1 ? 'unit' : 'units'}
												{row.unmatched_units ? ` · ${row.unmatched_units} unpriced` : ''}
											</span>
										</span>
									</button>
								</div>
							);
						})}
					</div>

					{totalCptPages > 1 ? (
						<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-4">
							<div className="small text-muted">
								Showing {cptPageStart + 1}-{Math.min(cptPageStart + CPTS_PER_PAGE, filteredCpts.length)} of {filteredCpts.length}
							</div>
							<div className="d-flex justify-content-end gap-2 flex-grow-1 flex-sm-grow-0">
								<button
									type="button"
									className="btn btn-sm hc-btn-prev px-4 px-sm-5 rounded-3 py-2 shadow flex-fill flex-sm-grow-0"
									disabled={safeCptPage <= 1}
									onClick={() => setCptPage((p) => Math.max(1, p - 1))}
								>
									Prev
								</button>
								<button
									type="button"
									className="btn btn-sm hc-btn-next border-0 px-4 px-sm-5 py-2 rounded-3 shadow flex-fill flex-sm-grow-0"
									disabled={safeCptPage >= totalCptPages}
									onClick={() => setCptPage((p) => Math.min(totalCptPages, p + 1))}
								>
									Next
								</button>
							</div>
						</div>
					) : null}
				</>
			)}

			{!loading && (percentageNote || unmatchedNote) ? (
				<div className="mt-3 d-flex flex-column gap-1">
					{percentageNote ? <p className="text-secondary small mb-0">{percentageNote}</p> : null}
					{unmatchedNote ? <p className="text-warning small mb-0">{unmatchedNote}</p> : null}
				</div>
			) : null}
		</div>
	);
}
